import React, { useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import { Api } from "../services/Api";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { Checkbox } from "primereact/checkbox";
import { Toast } from "primereact/toast";

function NotificacaoDialog({ close, notificacoes, showControls = true }) {
  const lista = useMemo(() => (Array.isArray(notificacoes) ? notificacoes : []), [notificacoes]);
  const toast = useRef(null);

  // Estados por item
  const [pdfUrls, setPdfUrls] = useState({}); // { [arquivoId]: objectUrl }
  const [loadingSet, setLoadingSet] = useState(new Set());
  const [errorById, setErrorById] = useState({});
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const preloadedRef = useRef(false);

  const resolveArquivoId = (item) => item?.notifica_id || item?.id || item?.uuid || item?.codigo || null;

  const base64ToBlob = (base64, contentType = "application/pdf") => {
    const pureBase64 = String(base64).includes(",") ? base64.split(",").pop() : base64;
    const byteChars = atob(pureBase64);
    const byteNums = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) byteNums[i] = byteChars.charCodeAt(i);
    const byteArray = new Uint8Array(byteNums);
    return new Blob([byteArray], { type: contentType });
  };

  useEffect(() => {
    return () => {
      Object.values(pdfUrls).forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch {}
      });
    };
  }, [pdfUrls]);

  const setLoading = (id, isLoading) => {
    setLoadingSet((prev) => {
      const copy = new Set(prev);
      if (isLoading) copy.add(id);
      else copy.delete(id);
      return copy;
    });
  };

  const carregarPDF = async (item, { forcar = false } = {}) => {
    const arquivoId = resolveArquivoId(item);
    if (!arquivoId) {
      setErrorById((p) => ({ ...p, __semId__: "ID do arquivo não encontrado." }));
      return;
    }
    if (!forcar && pdfUrls[arquivoId]) return;

    setLoading(arquivoId, true);
    setErrorById((p) => ({ ...p, [arquivoId]: null }));

    try {
      const { data } = await axios.post(
        "https://blob.paguedireto.com.br/receive_blob",
        { nome_arquivo: arquivoId, empresa: "notifica/pdf" },
        { headers: { "Content-Type": "application/json" }, timeout: 30000 }
      );

      if (!data || !data.result) throw new Error("A API não retornou o campo 'result'.");

      if (forcar && pdfUrls[arquivoId]) {
        try {
          URL.revokeObjectURL(pdfUrls[arquivoId]);
        } catch {}
      }

      const blob = base64ToBlob(data.result, "application/pdf");
      const objectUrl = URL.createObjectURL(blob);
      setPdfUrls((prev) => ({ ...prev, [arquivoId]: objectUrl }));
    } catch (err) {
      console.error("Erro ao carregar PDF:", err);
      setErrorById((p) => ({ ...p, [arquivoId]: "Não foi possível carregar o PDF. Tente novamente." }));
    } finally {
      setLoading(arquivoId, false);
    }
  };

  const preloadTodos = async () => {
    if (preloadedRef.current) return;
    preloadedRef.current = true;

    const itensValidos = lista.map((it) => ({ it, id: resolveArquivoId(it) })).filter(({ id }) => !!id);

    for (let i = 0; i < itensValidos.length; i++) {
      const { it } = itensValidos[i];
      // eslint-disable-next-line no-await-in-loop
      await carregarPDF(it);
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 120));
    }
  };

  const handleSave = async () => {
    if (!dontShowAgain) {
      close();
      return;
    }

    setIsSubmitting(true);
    try {
      // Pega o notifica_id da primeira notificação
      const firstNotification = lista[0];
      const notificaId = resolveArquivoId(firstNotification);

      if (notificaId) {
        await Api.put(`/notificacao/desativarNotificacao`, {
          notifica_id: notificaId,
        });

        toast.current.show({
          severity: "success",
          summary: "Sucesso",
          detail: "Notificação desativada com sucesso",
          life: 3000,
        });

        // Aguarda um pouco para mostrar o toast antes de fechar
        setTimeout(() => {
          close();
        }, 1000);
      } else {
        close();
      }
    } catch (error) {
      console.error("Erro ao desativar notificação:", error);
      toast.current.show({
        severity: "error",
        summary: "Erro",
        detail: "Erro ao desativar notificação. Tente novamente.",
        life: 3000,
      });
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    close();
  };

  useEffect(() => {
    if (lista.length > 0) preloadTodos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lista]);

  return (
    <div className="boletosDialog">
      <Toast ref={toast} />

      <Dialog
        visible
        draggable={false}
        onHide={handleClose}
        onShow={preloadTodos}
        style={{ width: "70vw", maxWidth: "1400px" }}
        breakpoints={{ "1200px": "95vw", "768px": "98vw" }}
        className="protestoCustom"
        header={
          showControls ? (
            <div className="w-full">
              <div className="flex justify-evenly items-start mb-4">
                <div className="text-left">
                  <p className="text-sm uppercase tracking-wide text-emerald-800">Comunicado importante</p>
                  <h3 className="text-2xl font-lato-bold mt-1">Você recebeu uma notificação</h3>
                  <p className="text-base mt-1">O documento está disponível abaixo.</p>
                </div>

                {/* Checkbox e Botão Salvar no header */}
                <div className="flex flex-col items-center gap-4">
                  <div className="flex items-center">
                    <Checkbox
                      inputId="dontShowAgain"
                      checked={dontShowAgain}
                      onChange={(e) => setDontShowAgain(e.checked)}
                      disabled={isSubmitting}
                    />
                    <label
                      htmlFor="dontShowAgain"
                      className="ml-2 text-sm text-gray-700 cursor-pointer whitespace-nowrap"
                    >
                      Não mostrar esta notificação novamente
                    </label>
                  </div>

                  <button
                    onClick={handleSave}
                    disabled={isSubmitting}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-md transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <ProgressSpinner style={{ width: "16px", height: "16px" }} strokeWidth="4" />
                        Salvando...
                      </>
                    ) : (
                      "Salvar"
                    )}
                  </button>
                </div>
              </div>
            </div>
          ) : null
        }
      >
        {/* Container com apenas o PDF */}
        <div className="grid gap-4 max-h-[83vh] overflow-auto pr-1">
          {lista.map((item, index) => {
            const arquivoId = resolveArquivoId(item);
            const urlPdf = arquivoId ? pdfUrls[arquivoId] : null;
            const isLoading = arquivoId ? loadingSet.has(arquivoId) : false;
            const errorMsg = arquivoId ? errorById[arquivoId] : errorById["__semId__"];

            return (
              <div
                key={item?.id || `${arquivoId || "notificacao"}-${index}`}
                className="rounded-2xl overflow-hidden bg-white border border-slate-200"
              >
                {/* Área do viewer maior */}
                <div style={{ minHeight: "70vh" }}>
                  {isLoading && (
                    <div className="flex items-center gap-2 p-4">
                      <ProgressSpinner style={{ width: 24, height: 24 }} strokeWidth="4" />
                      <span className="text-sm text-slate-700">Carregando PDF…</span>
                    </div>
                  )}

                  {!isLoading && errorMsg && <div className="p-4 text-sm text-red-600">{errorMsg}</div>}

                  {!isLoading && !errorMsg && urlPdf && (
                    <iframe
                      title={`pdf-${arquivoId}`}
                      src={urlPdf}
                      className="w-full"
                      style={{ height: "80vh", border: 0 }}
                    />
                  )}
                </div>
              </div>
            );
          })}

          {lista.length === 0 && (
            <div className="text-center py-4 text-slate-600">Nenhuma notificação disponível no momento.</div>
          )}
        </div>
      </Dialog>
    </div>
  );
}

export default NotificacaoDialog;
